import { Tooltip, Typography } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import FlowRunDisplay from './FlowRunDisplay';
import { formatTime, jobIconStatusMap, twentyFourHoursAgo } from './utils';
import { parseISO } from 'date-fns';
import JobCountsBar from './JobRunCountBar';
import FlowResourceDisplay from './FlowResourceDisplay';
import { Link } from '../../utils/theme';
import { Flow as FlowType } from '../../types/flow.types';

interface Settings {
  timeZone: {
    current: string;
  };
}

interface Account {
  name: string;
}

type FlowTableRow = FlowType & {
  health?: string;
  nextRun?: import('../../types/flow.types').FlowRun | null;
  lastRun?: import('../../types/flow.types').FlowRun | null;
  jobsCounts?: Record<string, number>;
  entity_type?: string;
  type_name?: string;
  source_img_url?: string;
};

interface ColumnParams {
  row: FlowTableRow;
  value: unknown;
}

const getFlowsListColumns = (settings: Settings, account: Account) => {
  const formatTimeInTimeZone = formatTime(settings.timeZone.current);
  const columns = [
    {
      field: 'name',
      headerName: 'Flow Name',
      width: 300,
      sortable: false,
      filterable: true,
      renderCell: (params: ColumnParams) => {
        const { id, description, enabled, name, health } = params.row;
        return (
          <Tooltip
            title={
              <div>
                <Typography variant="caption" sx={{ fontSize: 10 }}>
                  id: {id}
                </Typography>
                <br />
                <Typography variant="caption" sx={{ fontSize: 10 }}>
                  status: {health} {!enabled && '(disabled)'}
                </Typography>
                <Typography variant="body2">
                  <em>{description}</em>
                </Typography>
              </div>
            }
          >
            <div
              style={{ display: 'flex', alignItems: 'center', height: '100%' }}
            >
              {!enabled && <PauseIcon color={'disabled'} />}
              {enabled && health === 'healthy' && jobIconStatusMap['success']}
              {enabled && health === 'unhealthy' && jobIconStatusMap['failed']}

              <Typography
                noWrap={true}
                variant="body1"
                sx={{
                  marginLeft:
                    enabled && !['healthy', 'unhealthy'].includes(health || '')
                      ? '34px'
                      : '10px',
                }}
              >
                <Link
                  style={{
                    textDecoration: 'underline',
                  }}
                  to={`/${account.name}/flows/${name}`}
                >
                  {name}
                </Link>
              </Typography>
            </div>
          </Tooltip>
        );
      },
    },

    {
      field: 'source',
      headerName: 'Source',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => {
        if (!params.row.source) return null;
        return <FlowResourceDisplay flow={params.row} direction="source" />;
      },
    },
    {
      field: 'destinations',
      headerName: 'Destination(s)',
      sortable: false,
      filterable: false,
      width: 150,
      renderCell: (params: ColumnParams) => {
        if (!params.row.destinations) return null;
        return (
          <FlowResourceDisplay flow={params.row} direction="destination" />
        );
      },
    },
    {
      field: 'nextRun',
      headerName: 'Next Sync Run',
      sortable: false,
      filterable: false,
      width: 200,
      renderCell: (params: ColumnParams) => {
        const nextRun = params.row.nextRun;
        return (
          <Link
            data-cy="next-sync-run-link"
            to={`/${account.name}/flows/${params.row.name}/syncs/${nextRun?.id}`}
          >
            <FlowRunDisplay
              timezone={settings.timeZone.current}
              nextOrLastRun={'next'}
              flowRun={nextRun ?? null}
            />
          </Link>
        );
      },
    },
    {
      field: 'lastRun',
      headerName: 'Last Sync Run',
      sortable: false,
      filterable: false,
      width: 200,
      renderCell: (params: ColumnParams) => {
        const lastRun = params.row.lastRun;
        return (
          <Link
            data-cy="last-sync-run-link"
            to={`/${account.name}/flows/${params.row.name}/syncs/${lastRun?.id}`}
          >
            <FlowRunDisplay
              timezone={settings.timeZone.current}
              nextOrLastRun={'last'}
              flowRun={lastRun ?? null}
            />
          </Link>
        );
      },
    },
    {
      field: 'jobsCounts',
      headerName: 'Sync Runs',
      sortable: false,
      filterable: false,
      width: 130,
      renderHeader: () => {
        return (
          <Tooltip
            title={formatTimeInTimeZone(
              parseISO(twentyFourHoursAgo.toISOString()),
            )}
          >
            <Typography
              fontWeight={700}
              variant="body2"
              textAlign={'left'}
              color="initial"
            >
              Sync Runs <br />
              <small style={{ fontWeight: 400 }}>
                since 24hrs ago
                <br /> {}
              </small>
            </Typography>
          </Tooltip>
        );
      },
      renderCell: (params: ColumnParams) => (
        <JobCountsBar key={params.row.id} jobCounts={params.value} />
      ),
    },
  ];

  return columns;
};
export default getFlowsListColumns;
